import {
  type AssignTarget,
  AUTOMATION_ACTION_TYPES,
  AUTOMATION_COMMENT_MAX,
  type AutomationAction,
  DESCRIPTION_MAX,
  NOTIFICATION_CHANNELS,
  NOTIFY_MAX_MINUTES,
  NOTIFY_MAX_RECIPIENTS,
  NOTIFY_MAX_REPEATS,
  NOTIFY_RECIPIENT_KINDS,
  NOTIFY_TEMPLATE_MAX,
  type NotifyRecipient,
  SUMMARY_MAX,
  TICKET_SCOPED_ACTION_TYPES,
  TICKET_SCOPED_RECIPIENT_KINDS,
} from '@servicetim/shared';
import {
  useCannedResponses,
  useGroups,
  useIssueTypes,
  usePriorities,
  useProjectRoles,
  useProjects,
  useWebhooks,
} from '@servicetim/ui/api';
import {
  AsyncPicker,
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Textarea,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import {
  type CatalogField,
  type FieldCatalog,
  SETTABLE_SYSTEM_FIELDS,
} from '../projects/catalog/field-catalog';
import { fieldOptions } from '../projects/conditions/field-condition-row';
import { ConditionValueControl } from '../projects/conditions/value-control';
import { removeAt, replaceAt } from '../projects/lib/list-utils';
import { FormRow } from '../projects/ui/form-row';
import { SimpleSelect } from '../projects/ui/simple-select';

const ACTION_LABELS: Record<AutomationAction['type'], string> = {
  set_field: 'Set a field',
  assign: 'Assign the ticket',
  transition: 'Run a transition',
  add_comment: 'Add a comment',
  notify: 'Send a notification',
  emit_event: 'Emit a custom event',
  create_ticket: 'Create a ticket',
  webhook: 'Call a webhook',
};

const settableField = (f: CatalogField) =>
  f.system ? SETTABLE_SYSTEM_FIELDS.has(f.key) : !f.archived;

export function newAction(type: AutomationAction['type'], catalog: FieldCatalog): AutomationAction {
  switch (type) {
    case 'set_field':
      return { type, field: catalog.fields.find(settableField)?.key ?? 'assignee_id', value: null };
    case 'assign':
      return { type, to: 'unassigned' };
    case 'transition':
      return { type, transitionKey: '' };
    case 'add_comment':
      return { type, body: '', visibility: 'internal' };
    case 'notify':
      return { type, channels: ['in_app'], recipients: [{ kind: 'assignee' }], maxRepeats: 10 };
    case 'emit_event':
      return { type, eventType: 'custom.' };
    case 'create_ticket':
      return { type, projectId: '', issueTypeId: '', summary: '' };
    case 'webhook':
      return { type, subscriptionId: '' };
  }
}

export interface ActionsEditorProps {
  value: readonly AutomationAction[];
  onChange: (next: AutomationAction[]) => void;
  catalog: FieldCatalog;
  /** The rule's own scope (`null` = tenant-wide); used to load roles/canned responses/issue types. */
  projectId: string | null | undefined;
  /** `trigger.type === 'schedule'`: ticket-scoped actions/recipients are invalid (no ticket). */
  scheduleRule: boolean;
  idPrefix: string;
}

/** Ordered list of `AutomationAction`s (S-07-01 … S-07-04): each type gets its own inline form. */
export function ActionsEditor({
  value,
  onChange,
  catalog,
  projectId,
  scheduleRule,
  idPrefix,
}: ActionsEditorProps) {
  const update = (index: number, next: AutomationAction) => onChange(replaceAt(value, index, next));
  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-fg-muted text-sm">No actions yet: the rule matches but does nothing.</p>
      ) : (
        <ol className="space-y-2">
          {value.map((action, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; position is the key
              key={`${idPrefix}-${index}-${action.type}`}
              className="space-y-2 rounded-md border border-border bg-surface-raised p-3"
            >
              <div className="flex items-center gap-2">
                <span className="text-fg-muted text-xs tabular-nums">{index + 1}.</span>
                <Badge variant="secondary">{ACTION_LABELS[action.type]}</Badge>
                {scheduleRule &&
                (TICKET_SCOPED_ACTION_TYPES as readonly string[]).includes(action.type) ? (
                  <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300 text-xs">
                    <AlertTriangle className="size-3.5" aria-hidden="true" />
                    needs a ticket; a schedule trigger runs without one
                  </span>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-auto"
                  aria-label={`Remove action ${index + 1}`}
                  onClick={() => onChange(removeAt(value, index))}
                >
                  <Trash2 />
                </Button>
              </div>
              <ActionFields
                idPrefix={`${idPrefix}-${index}`}
                action={action}
                onChange={(next) => update(index, next)}
                catalog={catalog}
                projectId={projectId}
                scheduleRule={scheduleRule}
              />
            </li>
          ))}
        </ol>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={value.length >= 20}>
            <Plus />
            Add action
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {AUTOMATION_ACTION_TYPES.map((type) => (
            <DropdownMenuItem
              key={type}
              onSelect={() => onChange([...value, newAction(type, catalog)])}
            >
              {ACTION_LABELS[type]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function ActionFields({
  idPrefix,
  action,
  onChange,
  catalog,
  projectId,
  scheduleRule,
}: {
  idPrefix: string;
  action: AutomationAction;
  onChange: (next: AutomationAction) => void;
  catalog: FieldCatalog;
  projectId: string | null | undefined;
  scheduleRule: boolean;
}) {
  switch (action.type) {
    case 'set_field':
      return (
        <SetFieldFields idPrefix={idPrefix} action={action} onChange={onChange} catalog={catalog} />
      );
    case 'assign':
      return (
        <AssignFields
          idPrefix={idPrefix}
          action={action}
          onChange={onChange}
          projectId={projectId}
        />
      );
    case 'transition':
      return <TransitionFields idPrefix={idPrefix} action={action} onChange={onChange} />;
    case 'add_comment':
      return <AddCommentFields action={action} onChange={onChange} projectId={projectId} />;
    case 'notify':
      return (
        <NotifyFields
          idPrefix={idPrefix}
          action={action}
          onChange={onChange}
          projectId={projectId}
          scheduleRule={scheduleRule}
        />
      );
    case 'emit_event':
      return <EmitEventFields idPrefix={idPrefix} action={action} onChange={onChange} />;
    case 'create_ticket':
      return <CreateTicketFields idPrefix={idPrefix} action={action} onChange={onChange} />;
    case 'webhook':
      return <WebhookFields idPrefix={idPrefix} action={action} onChange={onChange} />;
  }
}

function SetFieldFields({
  idPrefix,
  action,
  onChange,
  catalog,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'set_field' }>;
  onChange: (next: AutomationAction) => void;
  catalog: FieldCatalog;
}) {
  const field = catalog.byKey.get(action.field);
  const tokens = field?.type === 'date' || field?.type === 'datetime' ? ['$now'] : [];
  return (
    <div className="flex flex-wrap items-start gap-2">
      <SimpleSelect
        id={`${idPrefix}-field`}
        aria-label="Field to set"
        size="sm"
        className="w-56"
        value={action.field}
        options={fieldOptions(catalog, (f) =>
          f.system ? SETTABLE_SYSTEM_FIELDS.has(f.key) : !f.archived,
        )}
        onChange={(next) => next && onChange({ type: 'set_field', field: next, value: null })}
      />
      <div className="min-w-56 flex-1">
        <ConditionValueControl
          id={`${idPrefix}-value`}
          field={field}
          op="eq"
          value={action.value === null ? undefined : action.value}
          allowMe={false}
          tokens={tokens}
          onChange={(next) => onChange({ ...action, value: next === undefined ? null : next })}
        />
      </div>
    </div>
  );
}

const ASSIGN_STRATEGY_OPTIONS = [
  { value: 'unassigned', label: 'Unassign' },
  { value: 'fixed', label: 'A specific user' },
  { value: 'round_robin', label: 'Round-robin over a pool' },
  { value: 'least_loaded', label: 'Least-loaded in a pool' },
];

function AssignFields({
  idPrefix,
  action,
  onChange,
  projectId,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'assign' }>;
  onChange: (next: AutomationAction) => void;
  projectId: string | null | undefined;
}) {
  const loadUsers = useUserOptionLoader();
  const roles = useProjectRoles(projectId ?? undefined);
  const groups = useGroups({ pageSize: 200 });
  const strategy: string = typeof action.to === 'string' ? action.to : action.to.strategy;
  const setTo = (to: AssignTarget) => onChange({ type: 'assign', to });

  const poolKind: 'role' | 'group' | undefined =
    typeof action.to === 'object' && action.to.strategy !== 'fixed'
      ? action.to.roleId !== undefined
        ? 'role'
        : action.to.groupId !== undefined
          ? 'group'
          : undefined
      : undefined;

  return (
    <div className="flex flex-wrap items-start gap-2">
      <SimpleSelect
        id={`${idPrefix}-strategy`}
        aria-label="Assign strategy"
        size="sm"
        className="w-56"
        value={strategy}
        options={ASSIGN_STRATEGY_OPTIONS}
        onChange={(next) => {
          if (!next) return;
          if (next === 'unassigned') setTo('unassigned');
          else if (next === 'fixed') setTo({ strategy: 'fixed', userId: '' });
          else
            setTo({
              strategy: next as 'round_robin' | 'least_loaded',
              roleId: roles.data?.[0]?.id ?? '',
            });
        }}
      />
      {typeof action.to === 'object' && action.to.strategy === 'fixed' ? (
        <div className="min-w-56 flex-1">
          <AsyncPicker
            id={`${idPrefix}-user`}
            value={action.to.userId || undefined}
            onChange={(next) => setTo({ strategy: 'fixed', userId: next ?? '' })}
            load={loadUsers}
            placeholder="Choose a user…"
          />
        </div>
      ) : null}
      {typeof action.to === 'object' && action.to.strategy !== 'fixed' ? (
        <>
          <SimpleSelect
            id={`${idPrefix}-pool-kind`}
            aria-label="Pool"
            size="sm"
            className="w-32"
            value={poolKind}
            options={[
              { value: 'role', label: 'Project role' },
              { value: 'group', label: 'Group' },
            ]}
            onChange={(next) => {
              if (!next || typeof action.to === 'string' || action.to.strategy === 'fixed') return;
              setTo(
                next === 'role'
                  ? { strategy: action.to.strategy, roleId: roles.data?.[0]?.id ?? '' }
                  : { strategy: action.to.strategy, groupId: groups.items[0]?.id ?? '' },
              );
            }}
          />
          {poolKind === 'role' ? (
            <SimpleSelect
              id={`${idPrefix}-role`}
              aria-label="Role"
              size="sm"
              className="w-56"
              value={
                typeof action.to === 'object' && 'roleId' in action.to
                  ? action.to.roleId
                  : undefined
              }
              placeholder={projectId ? 'Choose a role…' : 'Choose a project first'}
              options={(roles.data ?? []).map((r) => ({ value: r.id, label: r.name }))}
              disabled={!projectId}
              onChange={(roleId) =>
                roleId &&
                typeof action.to === 'object' &&
                action.to.strategy !== 'fixed' &&
                setTo({ strategy: action.to.strategy, roleId })
              }
            />
          ) : null}
          {poolKind === 'group' ? (
            <SimpleSelect
              id={`${idPrefix}-group`}
              aria-label="Group"
              size="sm"
              className="w-56"
              value={
                typeof action.to === 'object' && 'groupId' in action.to
                  ? action.to.groupId
                  : undefined
              }
              placeholder="Choose a group…"
              options={groups.items.map((g) => ({ value: g.id, label: g.name }))}
              onChange={(groupId) =>
                groupId &&
                typeof action.to === 'object' &&
                action.to.strategy !== 'fixed' &&
                setTo({ strategy: action.to.strategy, groupId })
              }
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function TransitionFields({
  idPrefix,
  action,
  onChange,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'transition' }>;
  onChange: (next: AutomationAction) => void;
}) {
  const hasComment = action.comment !== undefined;
  return (
    <div className="space-y-2">
      <Input
        aria-label="Transition key"
        placeholder="Transition key (matches by key across statuses)"
        value={action.transitionKey ?? ''}
        maxLength={40}
        className="h-8 w-72 font-mono text-xs"
        onChange={(e) =>
          onChange({
            type: 'transition',
            transitionKey: e.target.value,
            fields: action.fields,
            comment: action.comment,
          })
        }
      />
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-has-comment`}
          checked={hasComment}
          onCheckedChange={(checked) =>
            onChange({
              ...action,
              comment: checked === true ? { body: '', visibility: 'internal' } : undefined,
            })
          }
        />
        <label htmlFor={`${idPrefix}-has-comment`} className="text-fg-muted text-xs">
          Add a comment with the transition
        </label>
      </div>
      {action.comment ? (
        <div className="space-y-2">
          <SimpleSelect
            aria-label="Comment visibility"
            size="sm"
            className="w-40"
            value={action.comment.visibility}
            options={[
              { value: 'internal', label: 'Internal note' },
              { value: 'public', label: 'Public reply' },
            ]}
            onChange={(next) =>
              next &&
              action.comment &&
              onChange({
                ...action,
                comment: { ...action.comment, visibility: next as 'public' | 'internal' },
              })
            }
          />
          <Textarea
            aria-label="Comment body"
            value={action.comment.body}
            maxLength={60_000}
            rows={2}
            onChange={(e) =>
              action.comment &&
              onChange({ ...action, comment: { ...action.comment, body: e.target.value } })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function AddCommentFields({
  action,
  onChange,
  projectId,
}: {
  action: Extract<AutomationAction, { type: 'add_comment' }>;
  onChange: (next: AutomationAction) => void;
  projectId: string | null | undefined;
}) {
  const canned = useCannedResponses({ projectId: projectId ?? undefined, pageSize: 200 });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <SimpleSelect
          aria-label="Comment visibility"
          size="sm"
          className="w-40"
          value={action.visibility}
          options={[
            { value: 'internal', label: 'Internal note' },
            { value: 'public', label: 'Public reply' },
          ]}
          onChange={(next) =>
            next && onChange({ ...action, visibility: next as 'public' | 'internal' })
          }
        />
        {canned.items.length > 0 ? (
          <SimpleSelect
            aria-label="Insert canned response"
            size="sm"
            className="w-64"
            value={action.cannedResponseId}
            emptyLabel="Type a body instead"
            options={canned.items.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(cannedResponseId) =>
              onChange({
                ...action,
                cannedResponseId,
                body: cannedResponseId ? undefined : action.body,
              })
            }
          />
        ) : null}
      </div>
      {action.cannedResponseId === undefined ? (
        <Textarea
          aria-label="Comment body"
          value={action.body ?? ''}
          maxLength={AUTOMATION_COMMENT_MAX}
          rows={3}
          placeholder="Supports {{ticket.key}} / {{project.name}} placeholders."
          onChange={(e) => onChange({ ...action, body: e.target.value })}
        />
      ) : (
        <p className="text-fg-muted text-xs">Using the canned response's body and visibility.</p>
      )}
    </div>
  );
}

const NOTIFY_RECIPIENT_LABELS: Record<(typeof NOTIFY_RECIPIENT_KINDS)[number], string> = {
  reporter: 'the reporter',
  assignee: 'the assignee',
  watchers: 'watchers',
  role: 'a project role',
  user: 'a specific user',
  group: 'a group',
};

function NotifyFields({
  idPrefix,
  action,
  onChange,
  projectId,
  scheduleRule,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'notify' }>;
  onChange: (next: AutomationAction) => void;
  projectId: string | null | undefined;
  scheduleRule: boolean;
}) {
  const loadUsers = useUserOptionLoader();
  const roles = useProjectRoles(projectId ?? undefined);
  const groups = useGroups({ pageSize: 200 });

  const setRecipient = (index: number, next: NotifyRecipient) =>
    onChange({ ...action, recipients: replaceAt(action.recipients, index, next) });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {NOTIFICATION_CHANNELS.map((channel) => {
          const channelId = `${idPrefix}-channel-${channel}`;
          return (
            <span key={channel} className="flex items-center gap-1.5 text-fg-muted text-xs">
              <Checkbox
                id={channelId}
                checked={action.channels.includes(channel)}
                onCheckedChange={(checked) =>
                  onChange({
                    ...action,
                    channels:
                      checked === true
                        ? [...action.channels, channel]
                        : action.channels.filter((c) => c !== channel),
                  })
                }
              />
              <label htmlFor={channelId}>{channel === 'in_app' ? 'In-app' : 'Email'}</label>
            </span>
          );
        })}
      </div>
      <div className="space-y-1.5">
        <span className="font-medium text-fg-muted text-xs">Recipients</span>
        <ul className="space-y-1.5">
          {action.recipients.map((r, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; position is the key
            <li key={`${idPrefix}-r-${index}`} className="flex flex-wrap items-center gap-2">
              <SimpleSelect
                aria-label="Recipient kind"
                size="sm"
                className="w-44"
                value={r.kind}
                options={NOTIFY_RECIPIENT_KINDS.map((k) => ({
                  value: k,
                  label: NOTIFY_RECIPIENT_LABELS[k],
                }))}
                onChange={(next) => {
                  if (!next) return;
                  const kind = next as NotifyRecipient['kind'];
                  if (kind === 'reporter' || kind === 'assignee' || kind === 'watchers') {
                    setRecipient(index, { kind });
                  } else if (kind === 'role') {
                    setRecipient(index, { kind, roleId: roles.data?.[0]?.id ?? '' });
                  } else if (kind === 'group') {
                    setRecipient(index, { kind, groupId: groups.items[0]?.id ?? '' });
                  } else {
                    setRecipient(index, { kind, userId: '' });
                  }
                }}
              />
              {scheduleRule &&
              (TICKET_SCOPED_RECIPIENT_KINDS as readonly string[]).includes(r.kind) ? (
                <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300 text-xs">
                  <AlertTriangle className="size-3.5" aria-hidden="true" />
                  needs a ticket
                </span>
              ) : null}
              {r.kind === 'role' ? (
                <SimpleSelect
                  aria-label="Role"
                  size="sm"
                  className="w-56"
                  value={r.roleId || undefined}
                  placeholder={projectId ? 'Choose a role…' : 'Choose a project first'}
                  disabled={!projectId}
                  options={(roles.data ?? []).map((role) => ({ value: role.id, label: role.name }))}
                  onChange={(roleId) => roleId && setRecipient(index, { kind: 'role', roleId })}
                />
              ) : null}
              {r.kind === 'group' ? (
                <SimpleSelect
                  aria-label="Group"
                  size="sm"
                  className="w-56"
                  value={r.groupId || undefined}
                  placeholder="Choose a group…"
                  options={groups.items.map((g) => ({ value: g.id, label: g.name }))}
                  onChange={(groupId) => groupId && setRecipient(index, { kind: 'group', groupId })}
                />
              ) : null}
              {r.kind === 'user' ? (
                <div className="min-w-56 flex-1">
                  <label htmlFor={`${idPrefix}-r-${index}-user`} className="sr-only">
                    User
                  </label>
                  <AsyncPicker
                    id={`${idPrefix}-r-${index}-user`}
                    value={r.userId || undefined}
                    onChange={(userId) =>
                      setRecipient(index, { kind: 'user', userId: userId ?? '' })
                    }
                    load={loadUsers}
                    placeholder="Choose a user…"
                  />
                </div>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove recipient ${index + 1}`}
                onClick={() =>
                  onChange({ ...action, recipients: removeAt(action.recipients, index) })
                }
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={action.recipients.length >= NOTIFY_MAX_RECIPIENTS}
          onClick={() =>
            onChange({ ...action, recipients: [...action.recipients, { kind: 'assignee' }] })
          }
        >
          <Plus /> Add recipient
        </Button>
      </div>
      <Textarea
        aria-label="Message"
        value={action.template ?? ''}
        maxLength={NOTIFY_TEMPLATE_MAX}
        rows={2}
        placeholder="Message with {{ticket.key}}-style placeholders (optional; a channel default is used otherwise)."
        onChange={(e) =>
          onChange({ ...action, template: e.target.value === '' ? undefined : e.target.value })
        }
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <FormRow
          label="Delay (minutes)"
          htmlFor={`${idPrefix}-delay`}
          hint="Optional; 0 or empty sends immediately."
        >
          <Input
            id={`${idPrefix}-delay`}
            type="number"
            min={0}
            max={NOTIFY_MAX_MINUTES}
            value={action.delayMinutes ?? ''}
            onChange={(e) =>
              onChange({
                ...action,
                delayMinutes: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </FormRow>
        <FormRow label="Repeat every (minutes)" htmlFor={`${idPrefix}-repeat`} hint="Optional.">
          <Input
            id={`${idPrefix}-repeat`}
            type="number"
            min={1}
            max={NOTIFY_MAX_MINUTES}
            value={action.repeatEveryMinutes ?? ''}
            onChange={(e) =>
              onChange({
                ...action,
                repeatEveryMinutes: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        </FormRow>
        <FormRow label="Max repeats" htmlFor={`${idPrefix}-max-repeats`}>
          <Input
            id={`${idPrefix}-max-repeats`}
            type="number"
            min={1}
            max={NOTIFY_MAX_REPEATS}
            value={action.maxRepeats}
            onChange={(e) => onChange({ ...action, maxRepeats: Number(e.target.value) || 1 })}
          />
        </FormRow>
      </div>
      <span className="flex items-center gap-1.5 text-fg-muted text-xs">
        <Checkbox
          id={`${idPrefix}-repeat-until-ack`}
          checked={action.repeatUntilAcknowledged ?? false}
          onCheckedChange={(checked) =>
            onChange({ ...action, repeatUntilAcknowledged: checked === true })
          }
        />
        <label htmlFor={`${idPrefix}-repeat-until-ack`}>
          Repeat until the ticket is acknowledged
        </label>
      </span>
    </div>
  );
}

function EmitEventFields({
  idPrefix,
  action,
  onChange,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'emit_event' }>;
  onChange: (next: AutomationAction) => void;
}) {
  return (
    <Input
      aria-label="Custom event type"
      value={action.eventType}
      maxLength={80}
      className="h-8 w-72 font-mono text-xs"
      placeholder="custom.something_happened"
      onChange={(e) =>
        onChange({ type: 'emit_event', eventType: e.target.value, data: action.data })
      }
      id={`${idPrefix}-event-type`}
    />
  );
}

function CreateTicketFields({
  idPrefix,
  action,
  onChange,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'create_ticket' }>;
  onChange: (next: AutomationAction) => void;
}) {
  const projects = useProjects({ pageSize: 200 });
  const issueTypes = useIssueTypes(action.projectId || undefined);
  const priorities = usePriorities(action.projectId || undefined);
  const loadUsers = useUserOptionLoader();
  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormRow label="Project" htmlFor={`${idPrefix}-project`}>
          <SimpleSelect
            id={`${idPrefix}-project`}
            value={action.projectId || undefined}
            placeholder="Choose a project…"
            options={projects.items.map((p) => ({ value: p.id, label: `${p.key} · ${p.name}` }))}
            onChange={(projectId) =>
              projectId &&
              onChange({ ...action, projectId, issueTypeId: '', priorityId: undefined })
            }
          />
        </FormRow>
        <FormRow label="Issue type" htmlFor={`${idPrefix}-issue-type`}>
          <SimpleSelect
            id={`${idPrefix}-issue-type`}
            value={action.issueTypeId || undefined}
            placeholder={action.projectId ? 'Choose an issue type…' : 'Choose a project first'}
            disabled={!action.projectId}
            options={(issueTypes.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
            onChange={(issueTypeId) => issueTypeId && onChange({ ...action, issueTypeId })}
          />
        </FormRow>
      </div>
      <FormRow label="Summary" htmlFor={`${idPrefix}-summary`}>
        <Input
          id={`${idPrefix}-summary`}
          value={action.summary}
          maxLength={SUMMARY_MAX}
          placeholder="Supports {{project.name}}-style placeholders."
          onChange={(e) => onChange({ ...action, summary: e.target.value })}
        />
      </FormRow>
      <FormRow label="Description (optional)" htmlFor={`${idPrefix}-description`}>
        <Textarea
          id={`${idPrefix}-description`}
          value={action.description ?? ''}
          maxLength={DESCRIPTION_MAX}
          rows={2}
          onChange={(e) => onChange({ ...action, description: e.target.value || undefined })}
        />
      </FormRow>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormRow label="Priority (optional)" htmlFor={`${idPrefix}-priority`}>
          <SimpleSelect
            id={`${idPrefix}-priority`}
            value={action.priorityId}
            emptyLabel="Project default"
            options={(priorities.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            onChange={(priorityId) => onChange({ ...action, priorityId })}
          />
        </FormRow>
        <FormRow label="Assignee (optional)" htmlFor={`${idPrefix}-assignee`}>
          <AsyncPicker
            id={`${idPrefix}-assignee`}
            value={action.assigneeId}
            onChange={(assigneeId) => onChange({ ...action, assigneeId })}
            load={loadUsers}
            placeholder="Leave unassigned…"
          />
        </FormRow>
      </div>
    </div>
  );
}

function WebhookFields({
  idPrefix,
  action,
  onChange,
}: {
  idPrefix: string;
  action: Extract<AutomationAction, { type: 'webhook' }>;
  onChange: (next: AutomationAction) => void;
}) {
  const webhooks = useWebhooks(200);
  return (
    <SimpleSelect
      id={`${idPrefix}-subscription`}
      aria-label="Webhook subscription"
      value={action.subscriptionId || undefined}
      placeholder={webhooks.isPending ? 'Loading webhooks…' : 'Choose a subscription…'}
      options={webhooks.items.map((w) => ({ value: w.id, label: w.name }))}
      onChange={(subscriptionId) => subscriptionId && onChange({ type: 'webhook', subscriptionId })}
    />
  );
}
